import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNotificationContext } from '../../context/NotificationContext';

/**
 * NotificationTest Page
 * 
 * Test page for Email and Teams notifications
 * Located at /dev/notifications
 */
const NotificationTest = () => {
    const { user } = useAuth();
    const { success: showSuccess, error: showError } = useNotificationContext();
    
    // State
    const [config, setConfig] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [stages, setStages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [testResults, setTestResults] = useState([]);
    
    // Form state
    const [formData, setFormData] = useState({
        type: 'email',
        template: 'approval_pending',
        recipients: '',
        formId: 'business-trip',
        stage: '',
        customSubject: '',
        customMessage: '',
        // Custom data fields
        applicant_name: '',
        applicant_department: '',
        destination: '',
        total_estimated_cost: 15000,
    });

    // Fetch config and templates
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [configRes, templatesRes, stagesRes] = await Promise.all([
                    fetch('/api/notifications/config'),
                    fetch('/api/notifications/templates'),
                    fetch('/api/notifications/stages')
                ]);
                
                const configData = await configRes.json();
                const templatesData = await templatesRes.json();
                const stagesData = await stagesRes.json();
                
                setConfig(configData.data);
                setTemplates(templatesData.data || []);
                setStages(stagesData.data || []);
                
                // Set default recipient to current user
                if (user?.email || user?.mail) {
                    setFormData(prev => ({
                        ...prev,
                        recipients: user.email || user.mail,
                        applicant_name: user.displayName || '',
                        applicant_department: user.department || ''
                    }));
                }
            } catch (err) {
                console.error('Failed to fetch notification config:', err);
                showError('Failed to load notification configuration');
            } finally {
                setLoading(false);
            }
        };
        
        fetchData();
    }, [user]);

    // Handle form change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Test SMTP connection
    const handleTestSmtp = async () => {
        setSending(true);
        try {
            const res = await fetch('/api/notifications/test/smtp', { method: 'POST' });
            const data = await res.json();
            
            if (data.success) {
                showSuccess('SMTP connection successful!');
                addTestResult('SMTP Test', 'success', 'Connection verified');
            } else {
                showError('SMTP connection failed');
                addTestResult('SMTP Test', 'error', data.message || 'Connection failed');
            }
        } catch (err) {
            showError('SMTP test failed: ' + err.message);
            addTestResult('SMTP Test', 'error', err.message);
        } finally {
            setSending(false);
        }
    };

    // Send test notification
    const handleSendTest = async () => {
        if (!formData.recipients.trim()) {
            showError('Please enter at least one recipient');
            return;
        }

        setSending(true);
        try {
            const recipients = formData.recipients.split(',').map(e => e.trim()).filter(Boolean);
            
            const payload = {
                type: formData.type,
                template: formData.template,
                recipients,
                subject: formData.customSubject || undefined,
                message: formData.customMessage || undefined,
                customData: {
                    applicant_name: formData.applicant_name || user?.displayName || 'Test User',
                    applicant_department: formData.applicant_department || user?.department || 'IT',
                    destination: formData.destination || 'Tokyo, Japan',
                    total_estimated_cost: formData.total_estimated_cost,
                    formId: formData.formId,
                    formName: formData.formId === 'business-trip' ? '出差申請單' : '招待費用申請'
                }
            };

            const res = await fetch('/api/notifications/test', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            
            const data = await res.json();
            
            if (data.success) {
                showSuccess(`Notification sent via ${formData.type}!`);
                addTestResult(
                    `${formData.type.toUpperCase()} - ${formData.template}`,
                    'success',
                    `Sent to: ${recipients.join(', ')}`,
                    data.data
                );
            } else {
                showError('Failed to send notification: ' + (data.error || 'Unknown error'));
                addTestResult(
                    `${formData.type.toUpperCase()} - ${formData.template}`,
                    'error',
                    data.error || 'Send failed',
                    data.data
                );
            }
        } catch (err) {
            showError('Send failed: ' + err.message);
            addTestResult(formData.type.toUpperCase(), 'error', err.message);
        } finally {
            setSending(false);
        }
    };

    // Add test result to history
    const addTestResult = (title, status, message, details = null) => {
        setTestResults(prev => [{
            id: Date.now(),
            timestamp: new Date().toLocaleTimeString(),
            title,
            status,
            message,
            details
        }, ...prev].slice(0, 20)); // Keep last 20 results
    };

    // Clear test results
    const clearResults = () => {
        setTestResults([]);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8 max-w-6xl">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <span className="text-3xl">🔔</span>
                    Notification Test Center
                </h1>
                <p className="text-gray-600 mt-2">
                    Test Email and Microsoft Teams notifications before deploying to production
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Configuration Status */}
                <div className="lg:col-span-1 space-y-6">
                    {/* Service Status */}
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <span>⚙️</span> Service Status
                        </h2>
                        
                        <div className="space-y-3">
                            {/* Overall Status */}
                            <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                                <span className="text-sm font-medium">Notification Service</span>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    config?.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                    {config?.enabled ? '✓ Enabled' : '✗ Disabled'}
                                </span>
                            </div>
                            
                            {/* SMTP Status */}
                            <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                                <div>
                                    <span className="text-sm font-medium">📧 Email (SMTP)</span>
                                    {config?.smtp?.host && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            Host: {config.smtp.host}
                                        </p>
                                    )}
                                </div>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    config?.smtp?.configured ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                    {config?.smtp?.configured ? '✓ Configured' : '! Not Set'}
                                </span>
                            </div>
                            
                            {/* Teams Status */}
                            <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                                <span className="text-sm font-medium">💬 Microsoft Teams</span>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    config?.teams?.configured ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                    {config?.teams?.configured ? '✓ Configured' : '! Not Set'}
                                </span>
                            </div>

                            {/* Test SMTP Button */}
                            {config?.smtp?.configured && (
                                <button
                                    onClick={handleTestSmtp}
                                    disabled={sending}
                                    className="w-full mt-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 text-sm"
                                >
                                    {sending ? 'Testing...' : 'Test SMTP Connection'}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Available Templates */}
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <span>📝</span> Templates ({templates.length})
                        </h2>
                        
                        <div className="space-y-2">
                            {templates.map(template => (
                                <button
                                    key={template.id}
                                    onClick={() => setFormData(prev => ({ ...prev, template: template.id }))}
                                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                                        formData.template === template.id
                                            ? 'border-blue-500 bg-blue-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <div className="text-sm font-medium text-gray-900">{template.name}</div>
                                    <div className="text-xs text-gray-500 mt-1 truncate">{template.subject}</div>
                                    <div className="flex gap-1 mt-2">
                                        {template.hasHtml && (
                                            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] rounded">HTML</span>
                                        )}
                                        {template.hasTeams && (
                                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] rounded">Teams</span>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Middle Column - Test Form */}
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <span>🧪</span> Send Test Notification
                        </h2>
                        
                        <div className="space-y-4">
                            {/* Notification Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Notification Type
                                </label>
                                <div className="flex gap-2">
                                    {['email', 'teams', 'both'].map(type => (
                                        <button
                                            key={type}
                                            onClick={() => setFormData(prev => ({ ...prev, type }))}
                                            className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                                                formData.type === type
                                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                            }`}
                                        >
                                            {type === 'email' && '📧 '}
                                            {type === 'teams' && '💬 '}
                                            {type === 'both' && '📧💬 '}
                                            {type.charAt(0).toUpperCase() + type.slice(1)}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Form Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Form Type
                                </label>
                                <select
                                    name="formId"
                                    value={formData.formId}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                >
                                    <option value="business-trip">出差申請單 (Business Trip)</option>
                                    <option value="entertainment-expense">招待費用 (Entertainment Expense)</option>
                                </select>
                            </div>

                            {/* Approval Stage */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Approval Stage (Scenario)
                                </label>
                                <select
                                    name="stage"
                                    value={formData.stage}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                >
                                    <option value="">-- Select Stage --</option>
                                    {stages
                                        .filter(s => !formData.formId || s.formId === formData.formId)
                                        .map(stage => (
                                            <option key={`${stage.formId}-${stage.stepId}`} value={stage.stepId}>
                                                {typeof stage.stepName === 'object' ? (stage.stepName.en || stage.stepName['zh-TW'] || JSON.stringify(stage.stepName)) : stage.stepName} ({stage.approverType})
                                                {stage.hasConditions && ' ⚡'}
                                            </option>
                                        ))
                                    }
                                </select>
                            </div>

                            {/* Recipients */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Recipients (comma separated)
                                </label>
                                <input
                                    type="text"
                                    name="recipients"
                                    value={formData.recipients}
                                    onChange={handleChange}
                                    placeholder="email1@example.com, email2@example.com"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                            </div>

                            {/* Applicant Info */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Applicant Name
                                    </label>
                                    <input
                                        type="text"
                                        name="applicant_name"
                                        value={formData.applicant_name}
                                        onChange={handleChange}
                                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Department
                                    </label>
                                    <input
                                        type="text"
                                        name="applicant_department"
                                        value={formData.applicant_department}
                                        onChange={handleChange}
                                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                                    />
                                </div>
                            </div>

                            {/* Trip Details */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Destination
                                    </label>
                                    <input
                                        type="text"
                                        name="destination"
                                        value={formData.destination}
                                        onChange={handleChange}
                                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Est. Cost
                                    </label>
                                    <input
                                        type="number"
                                        name="total_estimated_cost"
                                        value={formData.total_estimated_cost}
                                        onChange={handleChange}
                                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                                    />
                                </div>
                            </div>

                            {/* Custom Subject (optional) */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Custom Subject (optional)
                                </label>
                                <input
                                    type="text"
                                    name="customSubject"
                                    value={formData.customSubject}
                                    onChange={handleChange}
                                    placeholder="Leave empty to use template default"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                            </div>

                            {/* Send Button */}
                            <button
                                onClick={handleSendTest}
                                disabled={sending || !formData.recipients.trim()}
                                className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2"
                            >
                                {sending ? (
                                    <>
                                        <span className="animate-spin">⏳</span>
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <span>🚀</span>
                                        Send Test Notification
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Right Column - Test Results */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <span>📋</span> Test Results
                            </h2>
                            {testResults.length > 0 && (
                                <button
                                    onClick={clearResults}
                                    className="text-xs text-gray-500 hover:text-red-500"
                                >
                                    Clear All
                                </button>
                            )}
                        </div>
                        
                        {testResults.length === 0 ? (
                            <div className="text-center py-8 text-gray-400">
                                <span className="text-4xl">📭</span>
                                <p className="mt-2 text-sm">No test results yet</p>
                                <p className="text-xs">Send a test notification to see results here</p>
                            </div>
                        ) : (
                            <div className="space-y-3 max-h-[600px] overflow-y-auto">
                                {testResults.map(result => (
                                    <div
                                        key={result.id}
                                        className={`p-3 rounded-lg border ${
                                            result.status === 'success'
                                                ? 'bg-green-50 border-green-200'
                                                : 'bg-red-50 border-red-200'
                                        }`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span>{result.status === 'success' ? '✅' : '❌'}</span>
                                                    <span className="font-medium text-sm">{result.title}</span>
                                                </div>
                                                <p className="text-xs text-gray-600 mt-1">{result.message}</p>
                                            </div>
                                            <span className="text-xs text-gray-400">{result.timestamp}</span>
                                        </div>
                                        
                                        {result.details && (
                                            <details className="mt-2">
                                                <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                                                    Show Details
                                                </summary>
                                                <pre className="mt-2 p-2 bg-white rounded text-xs overflow-auto max-h-40">
                                                    {JSON.stringify(result.details, null, 2)}
                                                </pre>
                                            </details>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Environment Variables Guide */}
            <div className="mt-8 bg-gray-50 rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">🔧 Configuration Guide</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* SMTP Config */}
                    <div>
                        <h4 className="font-medium text-gray-800 mb-2">📧 SMTP (Email)</h4>
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                            <pre className="text-xs text-gray-600 overflow-x-auto">
{`# .env
SMTP_HOST=smtp.office365.com
SMTP_PORT=587
SMTP_SECURE=false
SMTP_USER=your-email@company.com
SMTP_PASS=your-password
SMTP_FROM_NAME=OA System
SMTP_FROM_EMAIL=no-reply@company.com`}
                            </pre>
                        </div>
                    </div>
                    
                    {/* Teams Config */}
                    <div>
                        <h4 className="font-medium text-gray-800 mb-2">💬 Microsoft Teams</h4>
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                            <pre className="text-xs text-gray-600 overflow-x-auto">
{`# .env
TEAMS_WEBHOOK_URL=https://outlook.office.com/webhook/...

# To get Webhook URL:
# 1. Open Teams channel
# 2. Click ... > Connectors
# 3. Add "Incoming Webhook"
# 4. Copy the webhook URL`}
                            </pre>
                        </div>
                    </div>
                </div>
                
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                        <strong>⚠️ Note:</strong> Make sure to restart the server after updating .env file.
                        Set <code className="bg-yellow-100 px-1 rounded">NOTIFICATION_ENABLED=true</code> to enable notifications.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default NotificationTest;
